import { useEffect, useRef, useState } from 'react';

const VERZOEGERUNG_MS = 250;

/**
 * Das Suchmuster, das `EntityPicker`, `ContactDialog` und `ComposeDialog`
 * sonst je einzeln nachbauen würden: kurz abwarten, bevor die Anfrage
 * rausgeht, währenddessen `loading` melden, und eine Antwort verwerfen, die
 * nicht mehr zur zuletzt angestoßenen Suche gehört — sonst kann eine
 * langsame alte Antwort eine neuere überschreiben. Das trifft nicht nur
 * überlappende Tastendrücke, sondern auch eine Suche, die schon beim
 * Öffnen des Dialogs unbedingt losläuft und mit dem ersten Tastendruck
 * überholt werden kann.
 *
 * `deps` bestimmt wie bei `useEffect`, wann neu gesucht wird. `fetcher`
 * selbst darf sich bei jedem Rendern neu bilden, ohne dafür die Wartezeit
 * neu anzustoßen — nur eine Änderung in `deps` tut das.
 */
export function useDebouncedSearch<T>(
  fetcher: () => Promise<T>,
  deps: React.DependencyList,
  options: { delay?: number; enabled?: boolean } = {},
): { data: T | undefined; loading: boolean; error: string | null } {
  const { delay = VERZOEGERUNG_MS, enabled = true } = options;
  const [data, setData] = useState<T | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  // Zählt jede angestoßene Suche durch. Eine Antwort zählt nur, wenn sie
  // noch zur zuletzt angestoßenen gehört — kommt sie später an, wurde sie
  // von einer neueren Suche überholt und wird verworfen.
  const generation = useRef(0);

  useEffect(() => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    const eigeneGeneration = ++generation.current;
    const timer = setTimeout(() => {
      void fetcherRef.current().then(
        (ergebnis) => {
          if (eigeneGeneration !== generation.current) return;
          setData(ergebnis);
          setLoading(false);
        },
        (err) => {
          // Eine ueberholte Absage darf die neuere Suche nicht abwuergen —
          // derselbe Generation-Wächter wie beim Erfolgsfall.
          if (eigeneGeneration !== generation.current) return;
          setError(err instanceof Error ? err.message : 'Suche fehlgeschlagen');
          setLoading(false);
        },
      );
    }, delay);
    return () => clearTimeout(timer);
    // `deps` steuert bewusst allein, wann neu gesucht wird — siehe
    // Doc-Kommentar oben.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, delay, ...deps]);

  return { data, loading, error };
}
